import { ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Shift } from '../../apiTypes';
import { DateRangeButtons } from '../../components/DateRangeButtons';
import PrimaryButton from '../../components/PrimaryButton';
import DuplicateModal from '../../components/Shifts/DuplicateModal';
import ShiftLine from '../../components/Shifts/ShiftsLine';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useCollection } from '../../utils/api';
import { dateLocaleMap, getMonthTimeRange, getWeekTimeRange } from '../../utils/dates';
import { getInitMap } from '../../utils/map';
import useMedia from '../../utils/useMedia';
import EditShift from './EditShift';

const Journal = () => {
  const { isMobile } = useMedia();
  const { lang } = useLocale();
  const { workers: allWorkers, worker } = useCafe();
  const { cafeSlug } = useAdmin();
  const [scale, setScale] = useState<'week' | 'month'>('week');
  const [timeRange, setTimeRange] = useState<[number, number]>(getWeekTimeRange());
  const [open, setOpen] = useState(false);
  const [openDuplicate, setOpenDuplicate] = useState(false);
  const [shift, setShift] = useState<DbDocument<Shift> | null>(null);
  const [initialDate, setInitialDate] = useState<Date | null>(null);
  const [initialWorkerId, setInitialWorkerId] = useState<string | null>(null);

  const { data: allShifts, isValidating } = useCollection<Shift>(`cafes/${cafeSlug}/shifts`, {
    orderBy: ['start', 'asc'],
    where: ['start', '>=', timeRange[0] || 0],
    where2: ['start', '<=', timeRange[1] || 0],
  });

  const shifts = useMemo(
    () =>
      worker?.credentials.view_work_shifts_of_other_employees
        ? allShifts
        : allShifts?.filter((s) => s.workerId === worker?.id),
    [allShifts, worker?.credentials.view_work_shifts_of_other_employees, worker?.id]
  );

  const workers = useMemo(
    () => (!worker || worker.credentials.view_work_shifts_of_other_employees ? allWorkers : [worker]),
    [allWorkers, worker]
  );

  const shiftsMap = useMemo(
    () =>
      (shifts || []).reduce(
        (acc, s) => ({ ...acc, [s.workerId]: acc[s.workerId] ? [...(acc[s.workerId] || []), s] : [s] }),
        getInitMap<DbDocument<Shift>[]>()
      ),
    [shifts]
  );

  const dates = useMemo(() => {
    const dates: Date[] = [];
    for (
      let date = new Date(timeRange[0] || 0);
      date.getTime() <= (timeRange[1] || 0);
      date.setDate(date.getDate() + 1)
    ) {
      dates.push(new Date(date));
    }
    return dates;
  }, [timeRange]);

  const todayDateString = useMemo(() => format(new Date(), 'dd.MM.yyyy'), []);

  const handleShiftEdit = useCallback((shift: DbDocument<Shift>) => {
    setShift(shift);
    setOpen(true);
  }, []);

  const handleDuplicate = useCallback((shift: DbDocument<Shift>) => {
    setShift(shift);
    setOpenDuplicate(true);
  }, []);

  const handleDayClick = useCallback(
    (date: Date, workerId: string) => {
      const canEdit =
        (workerId === worker?.id && worker.credentials.edit_own_work_shifts) ||
        (workerId !== worker?.id && worker?.credentials.edit_work_shifts_of_other_employees);
      if (canEdit) {
        setInitialDate(date);
        setInitialWorkerId(workerId);
        setOpen(true);
      }
    },
    [worker?.credentials.edit_own_work_shifts, worker?.credentials.edit_work_shifts_of_other_employees, worker?.id]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setShift(null);
    setInitialDate(null);
    setInitialWorkerId(null);
  }, []);

  const handleCloseDuplicate = useCallback(() => {
    setOpenDuplicate(false);
    setShift(null);
  }, []);

  useEffect(() => {
    setTimeRange(scale === 'week' ? getWeekTimeRange() : getMonthTimeRange());
  }, [scale]);

  return (
    <Box sx={{ maxWidth: 1200, m: '0 auto' }}>
      <Box display="flex" justifyContent="space-between" flexDirection={isMobile ? 'column' : 'row'} mb={3}>
        <DateRangeButtons range={timeRange} setRange={setTimeRange} scale={scale} />
        <div>
          <IconButton onClick={() => setScale('month')} disabled={scale === 'month'}>
            <ZoomOut />
          </IconButton>
          <IconButton sx={{ mr: 2 }} onClick={() => setScale('week')} disabled={scale === 'week'}>
            <ZoomIn />
          </IconButton>
          <PrimaryButton
            variant="contained"
            onClick={() => setOpen(true)}
            disabled={
              !(worker?.credentials.edit_own_work_shifts || worker?.credentials.edit_work_shifts_of_other_employees)
            }
          >
            {t.add_shift[lang]}
          </PrimaryButton>
        </div>
      </Box>

      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ minWidth: scale === 'month' ? 800 : 400 }}>
          <Box display="flex" px={1}>
            <Box width={176} flexShrink={0} />
            {dates.map((date) => {
              const timeStart = date.getTime();
              const timeEnd = timeStart + 86400000;
              const isToday = format(date, 'dd.MM.yyyy') === todayDateString;
              const dayIndex = date.getDay();
              const isWeekend = dayIndex === 6 || dayIndex === 0;

              return (
                <Box
                  key={timeStart}
                  left={`${((timeStart - (timeRange[0] || 0)) * 100) / ((timeRange[1] || 0) - (timeRange[0] || 0))}%`}
                  width={`${((timeEnd - timeStart) * 100) / ((timeRange[1] || 0) - (timeRange[0] || 0))}%`}
                  sx={{
                    bgcolor: isToday ? (t) => `${t.palette.info.light}20` : undefined,
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    color: isWeekend ? (t) => t.palette.warning.dark : undefined,
                  }}
                >
                  <Typography variant="body2" sx={{ px: '2px', pt: '2px' }}>
                    {date.getDate()}
                  </Typography>
                  <Typography variant="body2" sx={{ px: '2px', pb: '2px' }}>
                    {format(date, 'EEEEEE', { locale: dateLocaleMap[lang] })}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {workers.map((worker) => (
            <ShiftLine
              worker={worker}
              key={worker.id}
              start={timeRange[0] || 0}
              end={timeRange[1] || 0}
              shifts={shiftsMap[worker.id] || []}
              dates={dates}
              onDayClick={handleDayClick}
              onShiftEdit={handleShiftEdit}
              onDuplicate={handleDuplicate}
            />
          ))}
        </Box>
      </Box>

      {open && (
        <EditShift
          open={open}
          shift={shift}
          initialDate={initialDate}
          initialWorkerId={initialWorkerId}
          onClose={handleClose}
        />
      )}

      {openDuplicate && shift && <DuplicateModal open shift={shift} onClose={handleCloseDuplicate} />}
    </Box>
  );
};

export default Journal;
